import React, { createContext, useContext, useMemo, useRef } from 'react';
import upperFirst from 'lodash/upperFirst';
import { MobXProviderContext, Provider as LegacyMobXContextProvider } from 'mobx-react';

import { NoProviderError } from './errors';

export type ClassType<TClass extends object, TClassArgs extends unknown[]> = {
  new (...args: TClassArgs): TClass;
};

type StoreHookName<TStoreName extends string> = `use${Capitalize<TStoreName>}`;
type NewStoreHookName<TStoreName extends string> = `useNew${Capitalize<TStoreName>}`;
type ProviderComponentName<TStoreName extends string> = `${Capitalize<TStoreName>}Provider`;
type CreateTestProviderName<TStoreName extends string> = `create${Capitalize<TStoreName>}`;

type CreateTestProvider<TMobXStore extends object> = (
  mobXStoreInstance: TMobXStore,
) => ProviderWrapper;
export type ProviderWrapper = (props: { children: React.ReactNode }) => JSX.Element;
type ProviderComponentProps<
  TStoreConstructorParams extends unknown[],
  TMobXStore extends object,
> = {
  children: React.ReactNode;
} & (
  | {
      /** The constructor parameters to create a new instance of the MobX store. */
      constructorParams?: TStoreConstructorParams;
      store?: never;
    }
  | {
      constructorParams?: never;
      /** An existing instance of the MobX store, usually created via the useNewStore hook. */
      store?: TMobXStore;
    }
);
type ProviderComponent<TStoreConstructorParams extends unknown[], TMobXStore extends object> = (
  props: ProviderComponentProps<TStoreConstructorParams, TMobXStore>,
) => JSX.Element;
type InternalProviderComponentProps<
  TStoreConstructorParams extends unknown[],
  TMobXStore extends object,
> = {
  children: React.ReactNode;
  constructorParams: TStoreConstructorParams | undefined;
  store: TMobXStore | undefined;
};
type InternalProviderComponent<
  TStoreConstructorParams extends unknown[],
  TMobXStore extends object,
> = (props: InternalProviderComponentProps<TStoreConstructorParams, TMobXStore>) => JSX.Element;
type StoreContextHelpers<
  TMobXStore extends object,
  TMockMobXStore extends object,
  TStoreName extends string,
  TStoreConstuctorParams extends unknown[],
> = {
  /** Hook to access the MobX store in the current context. */
  [storeHookName in StoreHookName<TStoreName>]: () => TMobXStore;
} & {
  /** Create a new instance of the MobX store and access it. Should be given as a prop to the respective `Provider` component. */
  [newStoreHookName in NewStoreHookName<TStoreName>]: (
    ...storeConstructorParams: TStoreConstuctorParams
  ) => TMobXStore;
} & {
  /** A component to provide the MobX store to children components. */
  [providerComponentName in ProviderComponentName<TStoreName>]: ProviderComponent<
    TStoreConstuctorParams,
    TMobXStore
  >;
} & {
  /** Create a test provider that provides a MobX store. */
  [testProviderName in CreateTestProviderName<TStoreName>]: CreateTestProvider<
    TMobXStore | TMockMobXStore
  >;
};

type StoreContextOptions<
  TMockMobXStoreClass extends ClassType<object, unknown[]>,
  TStoreConstructorParams extends unknown[],
  TStoreName extends string,
> = {
  /**
   * The name of the MobX store, used to generate the names of the hook, provider, and test provider.
   * If you have legacy code using `inject` or `strictInject`, this should match those names used to reference the store, i.e. `storeName` in `inject('storeName')`.
   */
  mobXStoreName: TStoreName;
  /**
   * @deprecated Should no longer be used, instantiation of the real store should be done instead. This introduces test only dependencies into application code.
   * A mock class to use in tests, if the real MobX store is not suitable or too heavy-weight for testing.
   */
  MockMobXStore?: TMockMobXStoreClass;
  /**
   * A hook to provide the constructor parameters for the MobX store. This is useful when the constructor parameters are not known at the time of creating the store context.
   */
  useConstructorParams?: () => TStoreConstructorParams;
  /**
   * Use this flag when you have legacy code that accesses a MobX store through `strictInject` or `inject`.
   * @deprecated To enable multiple instances of the same context being used at a time, we prefer to use React's context rather than the context managed by MobX.
   */
  supportStrictInject?: boolean;
};

// eslint-disable-next-line functional/no-classes
// eslint-disable-next-line functional/no-classes
class NoConstructorParamsError extends Error {
  constructor(capitalizedStoreName: string) {
    super(
      `${capitalizedStoreName}Provider must be supplied with arguments through props or through the useConstructorParams option in createMobXStoreContext.`,
    );
    this.name = 'NoConstructorParamsError';
  }
}
// eslint-disable-next-line functional/no-classes
class MobXStoreChangeError extends Error {
  constructor(storeName: string) {
    super(
      `MobX store ${storeName} has changed after initialization. This is not allowed when using the supportStrictInject option. You must either remove the supportStrictInject option in your call to createMobXStoreContext, or wrap the constructor parameters or store in useMemo with 0 dependencies.`,
    );
    this.name = 'MobXStoreChangeError';
  }
}

// eslint-disable-next-line functional/no-classes
class UnmatchedMobXStoreError extends Error {
  constructor(storeName: string, existingValue: unknown) {
    const existingValueType = existingValue?.constructor?.name ?? typeof existingValue;
    super(
      `MobX store '${storeName}' was found in the MobX context but with a different type '${existingValueType}'. Check if the store being provided is the same class as the one requested by the hook.`,
    );
    this.name = 'MobXStoreNotFoundError';
  }
}

/**
 * Helper function to create sensible defaults when using React context to provide a MobX store.
 * The returned object contains a hook, a component, and a function to create a test provider.
 * The names of these returned properties are determined by the `mobxStoreName` option.
 */
export function createMobXStoreContext<
  TMobXStore extends object,
  TStoreConstructorParams extends unknown[],
  TMockMobXStore extends object,
  TMockStoreConstructorParams extends unknown[],
  TStoreName extends string,
>(
  MobXStore: ClassType<TMobXStore, TStoreConstructorParams>,
  options: StoreContextOptions<
    ClassType<TMockMobXStore, TMockStoreConstructorParams>,
    TStoreConstructorParams,
    TStoreName
  >,
): StoreContextHelpers<TMobXStore, TMockMobXStore, TStoreName, TStoreConstructorParams> {
  const MobXStoreContext = createContext<TMobXStore | null>(null);
  const storeName = options.mobXStoreName;
  const capitalizedStoreName = upperFirst(storeName);
  function useMobXStoreFromExistingLegacyProvider(): TMobXStore | null {
    // Checks if an existing MobX provider is present with the same type and store name and returns it if present.
    const mobXProviderContextValue = useContext(MobXProviderContext);
    if (
      typeof mobXProviderContextValue !== 'object' ||
      mobXProviderContextValue === null ||
      !(storeName in mobXProviderContextValue)
    ) {
      return null;
    }
    const typedMobXProviderContextValue = mobXProviderContextValue as Record<string, unknown>;
    if (
      !(typedMobXProviderContextValue[storeName] instanceof MobXStore) &&
      (!options.MockMobXStore ||
        !(typedMobXProviderContextValue[storeName] instanceof options.MockMobXStore))
    ) {
      throw new UnmatchedMobXStoreError(
        capitalizedStoreName,
        typedMobXProviderContextValue[storeName],
      );
    }
    return typedMobXProviderContextValue[storeName] as TMobXStore | null;
  }
  function useMobXStore(): TMobXStore {
    const mobXStore = useContext(MobXStoreContext);
    if (mobXStore === null) {
      throw new NoProviderError(capitalizedStoreName);
    }
    return mobXStore;
  }
  const Provider: InternalProviderComponent<TStoreConstructorParams, TMobXStore> = (props) => {
    const { children, constructorParams: constructorParamsFromProps, store } = props;
    const storeConstructorParamsFromHook = options.useConstructorParams?.();
    const storeConstructorParams = constructorParamsFromProps || storeConstructorParamsFromHook;
    const mobXStore = useMemo(() => {
      if (store) {
        return store;
      }
      if (!storeConstructorParams) {
        throw new NoConstructorParamsError(capitalizedStoreName);
      }
      return new MobXStore(...storeConstructorParams);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(storeConstructorParams ?? []), store]);
    return <MobXStoreContext.Provider value={mobXStore}>{children}</MobXStoreContext.Provider>;
  };
  /**
   * @deprecated Only enabled by the supportStrictInject option.
   */
  const Provider_DEPRECATED: ProviderWrapper = ({ children }) => {
    const mobXStore = useMobXStore();
    const mobXStoreChangeRef = useRef(0);
    const legacyMobXContextProviderProps = useMemo(() => {
      mobXStoreChangeRef.current += 1;
      if (mobXStoreChangeRef.current > 1) {
        throw new MobXStoreChangeError(storeName);
      }
      return { [storeName]: mobXStore };
    }, [mobXStore]);
    return (
      <LegacyMobXContextProvider {...legacyMobXContextProviderProps}>
        {children}
      </LegacyMobXContextProvider>
    );
  };

  const WrappedProvider: ProviderComponent<TStoreConstructorParams, TMobXStore> = (props) => {
    const { children, constructorParams, store } = props;
    let providerChildren = children;
    const existingMobXStore = useMobXStoreFromExistingLegacyProvider();
    if (existingMobXStore) {
      return (
        <MobXStoreContext.Provider value={existingMobXStore}>{children}</MobXStoreContext.Provider>
      );
    }
    if (options.supportStrictInject) {
      providerChildren = <Provider_DEPRECATED>{children}</Provider_DEPRECATED>;
    }
    return (
      <Provider constructorParams={constructorParams} store={existingMobXStore ?? store}>
        {providerChildren}
      </Provider>
    );
  };
  const createTestProvider: CreateTestProvider<TMobXStore | TMockMobXStore> = (mobXStore) => {
    if (
      !(mobXStore instanceof MobXStore) &&
      (!options.MockMobXStore || !(mobXStore instanceof options.MockMobXStore))
    ) {
      let unknownMobXStoreWarning = `create${capitalizedStoreName} was called with an instance of ${mobXStore.constructor.name} instead of real class ${MobXStore.name}`;
      if (options.MockMobXStore) {
        unknownMobXStoreWarning += ` or mock class ${options.MockMobXStore.name}`;
      }
      console.warn(unknownMobXStoreWarning);
    }
    const TestProvider: ProviderWrapper = ({ children }) => {
      return (
        <MobXStoreContext.Provider value={mobXStore as TMobXStore}>
          <LegacyMobXContextProvider {...{ [storeName]: mobXStore }}>
            {children}
          </LegacyMobXContextProvider>
        </MobXStoreContext.Provider>
      );
    };
    return TestProvider;
  };
  function useNewMobXStore(...constructorParams: TStoreConstructorParams): TMobXStore {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const mobXStore = useMemo(() => new MobXStore(...constructorParams), constructorParams);
    return mobXStore;
  }

  const useStoreHookName = `use${capitalizedStoreName}` as const;
  const providerComponentName = `${capitalizedStoreName}Provider` as const;
  const testProviderName = `create${capitalizedStoreName}` as const;
  const newStoreHookName = `useNew${capitalizedStoreName}` as const;
  // TypeScript is not smart enough to infer the keys of the object literal below, so we have to type cast it.
  const storeContextHelpers = {
    [useStoreHookName]: useMobXStore,
    [providerComponentName]: WrappedProvider,
    [testProviderName]: createTestProvider,
    [newStoreHookName]: useNewMobXStore,
  } as StoreContextHelpers<TMobXStore, TMockMobXStore, TStoreName, TStoreConstructorParams>;

  return storeContextHelpers;
}
